import {
  Button,
  CreateButton,
  Dropdown,
  ExportButton,
  Icons,
  Input,
  List,
  Menu,
  Modal,
  notification,
  Popconfirm,
  ShowButton,
  Space,
  Table,
  TableProps,
  Tag,
  Typography,
} from '@pankod/refine-antd';
import { IResourceComponentsProps, useCan, useNavigation } from '@pankod/refine-core';
import { SearchWithDateRange } from 'components/search/SearchWithDateRange';
import CustomTableWithPagination from 'components/table/custom-table';
import { ETransferStatus } from 'enums';
import { ITransferStock, PaginationHandler } from 'interfaces';
import { useCallback, useEffect, useState } from 'react';
import { RiArticleLine } from 'react-icons/ri';
import { SearchIndex } from 'services/meilisearch';
import {
  approveTransferStock,
  getTransferStockInvoice,
  getTransferStocks,
  getTransferStocksDropdown,
  rejectTransferStock,
  updateTransferStatus,
} from 'services/stock-transfer';
import { getActivityHistoryUrl } from 'utilities/activity-log';
import { downloadCSVFile } from 'utilities/file';
import { formatCurrency, getDateRangeOfLastXMonths, previewPdfFromBase64 } from 'utilities/shared';
import { getTransferStatusStyle } from 'utilities/transfer-status';
import styles from './style.module.css';
import MetaData from 'utilities/metadata';

const { Text } = Typography;

interface IQuery {
  startDate: string;
  endDate: string;
  searchString: string;
  page: number;
  limit: number;
}

export const TransferStockList: React.FC<IResourceComponentsProps> = () => {
  const [transferStock, setTransferStock] = useState<ITransferStock[]>([]);
  const [totalTransferStocks, setTotalTransferStocks] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [actionsDisable, setActionsDisable] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState<IQuery>(() => {
    const [startDate, endDate] = getDateRangeOfLastXMonths(3);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      searchString: '',
      page: 1,
      limit: 10,
    };
  });
  const { push } = useNavigation();

  const { data: canApproveData } = useCan({
    resource: 'stock-transfers',
    action: 'approve',
  });
  const canApprove = canApproveData?.can ?? false;

  const { data: canEditData } = useCan({
    resource: 'stock-transfers',
    action: 'edit',
    params: { id: 'any' },
  });
  const canEdit = canEditData?.can ?? false;

  const fetchTransferredStocks = useCallback(async (query: Partial<IQuery>) => {
    setIsLoading(true);
    try {
      const response = await getTransferStocks(query);
      setTransferStock(response.data);
      setTotalTransferStocks(response.total);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchTransferredStocks(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = async (values: {
    startDate?: string;
    endDate?: string;
    searchString: string;
  }) => {
    if (!values.startDate && !values.endDate) {
      await fetchTransferredStocks({
        ...query,
        ...values,
        startDate: undefined,
        endDate: undefined,
        page: 1,
      });
    } else {
      await fetchTransferredStocks({ ...query, ...values, page: 1 });
    }
    setQuery({ ...query, ...values, page: 1 });
  };

  const onExportButtonClick = async () => {
    setIsExporting(true);
    try {
      const transferredStocks = await getTransferStocksDropdown(query.startDate, query.endDate);

      const rows = [['Transfer ID', 'Date', 'To-Warehouse', 'From-Warehouse', 'Total Stock Value']];
      transferredStocks.forEach((element) => {
        rows.push([
          element.id,
          element.transferDate.toString(),
          element.toWarehouse.warehouseCode,
          element.fromWarehouse.warehouseCode,
          element.totalStockValue.toString(),
        ]);
      });
      downloadCSVFile(rows);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsExporting(false);
  };

  const pageHeaders = (
    <Space>
      <CreateButton type="primary" />
      <ExportButton loading={isExporting} onClick={onExportButtonClick} />
      <Button
        icon={<Icons.HistoryOutlined />}
        onClick={() => push(getActivityHistoryUrl('transferStock'))}
      >
        Activity
      </Button>
    </Space>
  );

  const onTableChange: TableProps<ITransferStock>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    const page = pagination.current ?? 1;
    const limit = pagination.pageSize ?? 10;
    await fetchTransferredStocks({ ...query, page: page, limit });
    setQuery({ ...query, page: page, limit });
  };

  const handleStatusUpdate = async (id: string, status: ETransferStatus) => {
    setActionsDisable((prev) => ({ ...prev, [id]: true }));
    try {
      await updateTransferStatus(id, status);
      await fetchTransferredStocks(query);
      notification.success({
        message: 'Success',
        description: 'Successfully changed status',
      });
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: err instanceof Error ? err.message : null,
      });
    } finally {
      setActionsDisable(({ [id]: _, ...rest }) => rest);
    }
  };

  const handleApprove = async (id: string) => {
    setActionsDisable((prev) => ({ ...prev, [id]: true }));
    try {
      await approveTransferStock(id);
      await fetchTransferredStocks(query);
      notification.success({
        message: 'Success',
        description: 'Transfer approved successfully',
      });
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: err instanceof Error ? err.message : null,
      });
    } finally {
      setActionsDisable(({ [id]: _, ...rest }) => rest);
    }
  };

  const handleReject = async (id: string, rejectionReason: string) => {
    setActionsDisable((prev) => ({ ...prev, [id]: true }));
    try {
      await rejectTransferStock(id, rejectionReason);
      await fetchTransferredStocks(query);
      notification.success({
        message: 'Success',
        description: 'Transfer rejected successfully',
      });
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: err instanceof Error ? err.message : null,
      });
    } finally {
      setActionsDisable(({ [id]: _, ...rest }) => rest);
    }
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    await fetchTransferredStocks({ ...query, page: page, limit: pageSize });
    setQuery({ ...query, page: page, limit: pageSize });
  };

  return (
    <List headerProps={{ extra: pageHeaders }}>
      <MetaData />
      <SearchWithDateRange
        placeholder="Search by ID, Warehouse"
        size="large"
        className={styles['filter-section__search']}
        index={SearchIndex.TransferStock}
        groups={['id', 'warehouse']}
        onSearch={onSearch}
      />
      <CustomTableWithPagination
        total={totalTransferStocks}
        page={query.page}
        limit={query.limit}
        onPageChange={onPageChange}
      >
        <Table
          dataSource={transferStock}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: true }}
          onChange={onTableChange}
          pagination={false}
        >
          <Table.Column dataIndex="id" title="Transfer ID" />
          <Table.Column<ITransferStock>
            dataIndex="transferDate"
            title="Date"
            render={(_, record): React.ReactNode => {
              const date = new Date(record.transferDate).toLocaleDateString('en-GB');
              return <Text>{date}</Text>;
            }}
          />
          <Table.Column
            dataIndex="transferStatus"
            title="Status"
            render={(status: string) => {
              const { textColor, backgroundColor } = getTransferStatusStyle(status);

              return (
                <Tag
                  style={{
                    color: textColor,
                    backgroundColor: backgroundColor,
                    height: '24px',
                    padding: '0px 8px',
                    gap: '4px',
                    borderRadius: '8px',
                    opacity: 1,
                    border: 'none',
                    fontFamily: 'Nunito Sans',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '22px',
                  }}
                >
                  {status}
                </Tag>
              );
            }}
          />
          <Table.Column dataIndex={['toWarehouse', 'name']} title="To-Warehouse" />
          <Table.Column dataIndex={['fromWarehouse', 'name']} title="From-Warehouse" />
          <Table.Column<ITransferStock>
            dataIndex="totalStockValue"
            title="Total Stock Value"
            render={(_, record) => formatCurrency(record.totalStockValue)}
          />
          <Table.Column<ITransferStock>
            title="Actions"
            dataIndex="actions"
            width={120}
            render={(_text, record): React.ReactNode => {
              return (
                <Space>
                  <ShowButton size="small" recordItemId={record.id} hideText />
                  <ReceiptButton transferStockId={record.id} />
                  {(canEdit || canApprove) && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <StatusUpdateAction
                        record={record}
                        handleStatusUpdate={handleStatusUpdate}
                        handleApprove={handleApprove}
                        handleReject={handleReject}
                        disabled={actionsDisable[record.id]}
                        canApprove={canApprove}
                        canEdit={canEdit}
                      />
                    </div>
                  )}
                </Space>
              );
            }}
          />
        </Table>
      </CustomTableWithPagination>
    </List>
  );
};

type ReceiptButtonProps = {
  transferStockId: string;
};

const ReceiptButton: React.FC<ReceiptButtonProps> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleClick = useCallback(async (id: string) => {
    setIsLoading(true);
    try {
      const invoiceString = await getTransferStockInvoice(id);
      previewPdfFromBase64(invoiceString);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <Button
      size="small"
      loading={isLoading}
      icon={<RiArticleLine size={16} style={{ marginTop: '3px' }} />}
      onClick={() => handleClick(props.transferStockId)}
    />
  );
};

type StatusUpdateActionProps = {
  record: ITransferStock;
  handleStatusUpdate: (id: string, status: ETransferStatus) => Promise<void>;
  handleApprove: (id: string) => Promise<void>;
  handleReject: (id: string, rejectionReason: string) => Promise<void>;
  disabled?: boolean;
  canApprove: boolean;
  canEdit: boolean;
};

const getDisabledState = (status: ETransferStatus | string) => {
  switch (status) {
    case ETransferStatus.PENDING_APPROVAL:
    case ETransferStatus.REJECTED:
      return {
        inTransitDisabled: true,
        inProgressDisabled: true,
        completedDisabled: true,
        cancelledDisabled: true,
      };
    case ETransferStatus.IN_TRANSIT:
      return {
        inTransitDisabled: true,
        inProgressDisabled: false,
        completedDisabled: false,
        cancelledDisabled: false,
      };
    case ETransferStatus.IN_PROGRESS:
      return {
        inTransitDisabled: true,
        inProgressDisabled: true,
        completedDisabled: false,
        cancelledDisabled: false,
      };
    case ETransferStatus.COMPLETED:
      return {
        inTransitDisabled: true,
        inProgressDisabled: true,
        completedDisabled: true,
        cancelledDisabled: false,
      };
    case ETransferStatus.CANCELLED:
      return {
        inTransitDisabled: true,
        inProgressDisabled: true,
        completedDisabled: true,
        cancelledDisabled: true,
      };
    default:
      return {
        inTransitDisabled: false,
        inProgressDisabled: false,
        completedDisabled: false,
        cancelledDisabled: false,
      };
  }
};

const StatusUpdateAction: React.FC<StatusUpdateActionProps> = ({
  record,
  handleStatusUpdate,
  handleApprove,
  handleReject,
  disabled,
  canApprove,
  canEdit,
}) => {
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  const isPendingApproval = record.transferStatus === ETransferStatus.PENDING_APPROVAL;
  const { inTransitDisabled, inProgressDisabled, completedDisabled, cancelledDisabled } =
    getDisabledState(record.transferStatus as ETransferStatus);

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) return;
    setRejectSubmitting(true);
    await handleReject(record.id, rejectReason.trim());
    setRejectSubmitting(false);
    setRejectModalOpen(false);
    setRejectReason('');
  };

  const moreMenu = (
    <Menu mode="vertical" onClick={({ domEvent }) => domEvent.stopPropagation()}>
      {isPendingApproval && canApprove && (
        <>
          <Popconfirm
            title="Approve this transfer?"
            okText="Approve"
            cancelText="Cancel"
            disabled={disabled}
            onConfirm={() => handleApprove(record.id)}
          >
            <Menu.Item key="approve" disabled={disabled}>
              Approve
            </Menu.Item>
          </Popconfirm>
          <Menu.Item
            key="reject"
            disabled={disabled}
            onClick={() => setRejectModalOpen(true)}
          >
            Reject
          </Menu.Item>
        </>
      )}
      {canEdit && (
        <>
          <Menu.Item
            key="transit"
            disabled={disabled || inTransitDisabled}
            onClick={() => handleStatusUpdate(record.id, ETransferStatus.IN_TRANSIT)}
          >
            In Transit
          </Menu.Item>
          <Menu.Item
            key="progress"
            disabled={disabled || inProgressDisabled}
            onClick={() => handleStatusUpdate(record.id, ETransferStatus.IN_PROGRESS)}
          >
            Delivery In Progress
          </Menu.Item>
          <Menu.Item
            key="completed"
            disabled={disabled || completedDisabled}
            onClick={() => handleStatusUpdate(record.id, ETransferStatus.COMPLETED)}
          >
            Completed
          </Menu.Item>
          <Popconfirm
            title="Are you sure?"
            okText="Yes"
            cancelText="No"
            disabled={disabled || cancelledDisabled}
            onConfirm={() => handleStatusUpdate(record.id, ETransferStatus.CANCELLED)}
          >
            <Menu.Item key="cancelled" disabled={disabled || cancelledDisabled}>
              Cancelled
            </Menu.Item>
          </Popconfirm>
        </>
      )}
    </Menu>
  );

  return (
    <>
      <Dropdown overlay={moreMenu} trigger={['click']}>
        <Icons.MoreOutlined
          onClick={(e) => e.stopPropagation()}
          style={{ fontSize: 24 }}
        />
      </Dropdown>

      <Modal
        title="Reject Transfer"
        visible={rejectModalOpen}
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectReason('');
        }}
        onOk={handleRejectConfirm}
        okText="Reject"
        okButtonProps={{ danger: true, disabled: !rejectReason.trim(), loading: rejectSubmitting }}
        destroyOnClose
      >
        <Input.TextArea
          rows={3}
          placeholder="Enter rejection reason (required)"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </>
  );
};
